/**
 * Десять концептів подарунка на день народження — МАКЕТИ (owner 2026-09-13:
 * «концепт — це візуальна картинка, а не текст»).
 *
 * Сторінка існує тільки щоб зняти з неї скріншоти й показати власнику. Це
 * статичні макети: клікати нічого не треба, логіки немає. Обраний концепт
 * потім робимо по-справжньому.
 */
import { Icon } from "@/components/Icon";
import { TgEmoji } from "@/components/TgEmoji";

const CREAM = "#F5F1E8";

function Frame({
  n,
  title,
  note,
  children,
}: {
  n: number;
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8" data-concept={n}>
      <div className="mb-2 flex items-baseline gap-2">
        <span className="font-mono text-[0.6875rem] text-primary">{n}</span>
        <span className="font-display text-[0.9375rem] font-bold text-ink">
          {title}
        </span>
      </div>
      <div className="mb-2 text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
        {note}
      </div>
      <div
        className="overflow-hidden rounded-[1.25rem] border border-ink/[0.08] shadow-card"
        style={{ background: CREAM }}
      >
        {children}
      </div>
    </div>
  );
}

/** Загальна «шторка» попапа — щоб макети виглядали як реальні попапи. */
function Sheet({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4">
      <div className="rounded-[1.25rem] bg-surface p-5 shadow-card">{children}</div>
    </div>
  );
}

function Pill({ text, tone = "soft" }: { text: string; tone?: "soft" | "solid" }) {
  return (
    <span
      className={
        "inline-block rounded-full px-2.5 py-1 font-display text-[0.6875rem] font-bold uppercase leading-none " +
        (tone === "solid"
          ? "bg-primary text-white"
          : "bg-primary/10 text-primary")
      }
    >
      {text}
    </span>
  );
}

function Cta({ label }: { label: string }) {
  return (
    <div className="mt-3 rounded-full bg-primary py-3 text-center font-display text-[0.875rem] font-bold text-white">
      {label}
    </div>
  );
}

export function BirthdayConceptsPage() {
  return (
    <div className="px-4 py-6">
      <div className="mb-1 font-mono text-[0.65625rem] uppercase tracking-[0.1em] text-ink-dim">
        Подарунок на день народження
      </div>
      <h1
        className="mb-6 font-display font-bold text-ink"
        style={{ fontSize: "1.5rem", lineHeight: 1.1 }}
      >
        10 концептів
      </h1>

      {/* 1 — коробка в попапі */}
      <Frame
        n={1}
        title="Подарункова коробка"
        note="Попап поверх головної: закрита коробка, тап — кришка злітає, конфеті, всередині три свёртки."
      >
        <Sheet>
          <div className="text-center">
            <div className="mx-auto mb-3 grid h-24 w-24 place-items-center rounded-[1.5rem] bg-primary/10">
              <TgEmoji char="🎁" size={52} />
            </div>
            <div className="font-display text-[1.0625rem] font-bold text-ink">
              З днем народження!
            </div>
            <div className="mt-1 text-[0.8125rem] leading-snug text-ink-dim">
              Тисни на коробку — подарунок усередині
            </div>
            <Cta label="Відкрити подарунок" />
          </div>
        </Sheet>
      </Frame>

      {/* 2 — три закриті коробки */}
      <Frame
        n={2}
        title="Три коробки — обери одну"
        note="Три однакові коробки з підказками. Відкриваєш одну — вона і є твій подарунок, інші лишаються закритими."
      >
        <Sheet>
          <div className="mb-3 text-center font-display text-[1rem] font-bold text-ink">
            Обери коробку
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["про час", "про вигоду", "найбільший"].map((t, i) => (
              <div
                key={t}
                className={
                  "rounded-[1rem] p-3 text-center " +
                  (i === 1 ? "bg-primary/10 ring-2 ring-primary/40" : "bg-bg")
                }
              >
                <TgEmoji char="🎁" size={30} />
                <div className="mt-1.5 text-[0.625rem] leading-tight text-ink-dim">
                  {t}
                </div>
              </div>
            ))}
          </div>
          <Cta label="Відкрити" />
        </Sheet>
      </Frame>

      {/* 3 — скретч-карта */}
      <Frame
        n={3}
        title="Скретч-карта"
        note="Стираєш пальцем сірий шар — під ним поздоровлення й подарунок. Половина вже стерта на макеті."
      >
        <Sheet>
          <div className="relative overflow-hidden rounded-[1rem] bg-bg p-4">
            <div className="text-center">
              <Pill text="твій подарунок" />
              <div className="mt-2 font-display text-[1.375rem] font-bold text-primary">
                −55% на місяць
              </div>
              <div className="text-[0.75rem] text-ink-dim">449 ₴ замість 990 ₴</div>
            </div>
            <div
              className="absolute inset-y-0 right-0 w-1/2"
              style={{
                background:
                  "repeating-linear-gradient(135deg,#c9c2b6,#c9c2b6 6px,#d8d2c6 6px,#d8d2c6 12px)",
              }}
            >
              <div className="grid h-full place-items-center text-[0.6875rem] font-bold uppercase text-white/90">
                стирай
              </div>
            </div>
          </div>
          <Cta label="Забрати" />
        </Sheet>
      </Frame>

      {/* 4 — іменинне колесо */}
      <Frame
        n={4}
        title="Іменинне колесо"
        note="Один оберт, зупиняється на подарунку. Компонент колеса вже є — найшвидший шлях."
      >
        <Sheet>
          <div className="text-center">
            <div className="relative mx-auto mb-3 h-32 w-32">
              <div
                className="h-32 w-32 rounded-full"
                style={{
                  background:
                    "conic-gradient(#FE6B04 0 33%, #FFD9BC 33% 66%, #1a1714 66% 100%)",
                }}
              />
              <div className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-surface shadow-card" />
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-primary">
                ▼
              </div>
            </div>
            <div className="font-display text-[1rem] font-bold text-ink">
              Крути — це твій день
            </div>
            <Cta label="Крутити" />
          </div>
        </Sheet>
      </Frame>

      {/* 5 — конверт з листівкою */}
      <Frame
        n={5}
        title="Конверт із листівкою"
        note="Конверт відкривається, всередині листівка з іменем. Свайп убік — під нею три подарунки."
      >
        <Sheet>
          <div className="rounded-[1rem] bg-bg p-4">
            <div className="mb-2 text-center">
              <TgEmoji char="📨" size={34} />
            </div>
            <div className="text-center font-serif italic text-[0.9375rem] text-ink">
              Єгоре, з днем народження!
            </div>
            <div className="mt-1 text-center text-[0.75rem] leading-snug text-ink-dim">
              Дякуємо, що ти з нами. Ось маленький подарунок від команди inFLOW.
            </div>
          </div>
          <div className="mt-2 text-center text-[0.6875rem] text-ink-faint">
            гортай листівку →
          </div>
        </Sheet>
      </Frame>

      {/* 6 — сторіс */}
      <Frame
        n={6}
        title="Сторіс на весь екран"
        note="Три екрани: привітання → твої цифри за рік → вибір подарунка. Смуги прогресу зверху."
      >
        <div className="relative h-[19rem] bg-dark p-4 text-white">
          <div className="mb-4 flex gap-1">
            {[100, 60, 0].map((w, i) => (
              <div key={i} className="h-0.5 flex-1 rounded-full bg-white/25">
                <div className="h-0.5 rounded-full bg-white" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <TgEmoji char="🎂" size={44} />
            <div className="mt-3 font-display text-[1.25rem] font-bold leading-tight">
              Ти з нами 214 днів
            </div>
            <div className="mt-2 text-[0.8125rem] leading-snug text-white/70">
              За цей час ти отримав 1 840 вакансій і згенерував 32 відповіді.
            </div>
            <div className="mt-4 inline-block rounded-full bg-primary px-5 py-2.5 font-display text-[0.8125rem] font-bold">
              Далі — подарунок
            </div>
          </div>
        </div>
      </Frame>

      {/* 7 — торт зі свічками */}
      <Frame
        n={7}
        title="Торт зі свічками"
        note="Задуй три свічки (тап або утримання) — конфеті, торт ділиться на три шматки = три подарунки."
      >
        <Sheet>
          <div className="text-center">
            <div className="mb-2 flex justify-center gap-3">
              {[1, 2, 3].map((i) => (
                <span key={i} className={i === 3 ? "opacity-30" : ""}>
                  <TgEmoji char="🎂" size={26} />
                </span>
              ))}
            </div>
            <TgEmoji char="🎂" size={56} />
            <div className="mt-2 font-display text-[1rem] font-bold text-ink">
              Задуй свічки
            </div>
            <div className="text-[0.75rem] text-ink-dim">лишилась одна</div>
          </div>
        </Sheet>
      </Frame>

      {/* 8 — підсумки року */}
      <Frame
        n={8}
        title="Підсумки року, подарунок у кінці"
        note="Спершу показуємо цінність — скільки людина отримала, — і лише потім пропонуємо подарунок."
      >
        <Sheet>
          <div className="mb-3 font-display text-[1rem] font-bold text-ink">
            Твій рік в inFLOW
          </div>
          <div className="space-y-2">
            {[
              ["Вакансій отримано", "1 840"],
              ["Відповідей згенеровано", "32"],
              ["Контактів відкрито", "97"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between rounded-xl bg-bg px-3 py-2"
              >
                <span className="text-[0.75rem] text-ink-dim">{k}</span>
                <span className="font-display text-[0.9375rem] font-bold text-ink tabular-nums">
                  {v}
                </span>
              </div>
            ))}
          </div>
          <Cta label="Забрати подарунок" />
        </Sheet>
      </Frame>

      {/* 9 — прямо в чаті */}
      <Frame
        n={9}
        title="Прямо в чаті, без аппки"
        note="Привітання і три кнопки під ним. Найкоротший шлях до оплати — для тих, хто аппку не відкриває."
      >
        <div className="p-4">
          <div className="rounded-[1rem] rounded-bl-sm bg-surface p-3 shadow-card-soft">
            <div className="font-display text-[0.875rem] font-bold text-ink">
              З днем народження 🧡
            </div>
            <div className="mt-1 text-[0.78125rem] leading-snug text-ink-dim">
              Обирай подарунок — він діє 14 днів.
            </div>
          </div>
          <div className="mt-1.5 space-y-1.5">
            {["14 днів за 1 ₴", "Місяць за 449 ₴ (−55%)", "2 місяці за 990 ₴"].map(
              (t) => (
                <div
                  key={t}
                  className="rounded-[0.75rem] bg-surface px-3 py-2 text-center font-display text-[0.8125rem] font-semibold text-primary shadow-card-soft"
                >
                  {t}
                </div>
              ),
            )}
          </div>
        </div>
      </Frame>

      {/* 10 — конструктор */}
      <Frame
        n={10}
        title="Конструктор подарунка"
        note="Повзунок «скільки днів хочу» — ціна перераховується на очах, у день народження ставка за день нижча."
      >
        <Sheet>
          <div className="mb-1 font-display text-[1rem] font-bold text-ink">
            Збери свій подарунок
          </div>
          <div className="mb-3 text-[0.75rem] text-ink-dim">
            Сьогодні день за 14 ₴ замість 33 ₴
          </div>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="font-display text-[1.75rem] font-bold text-primary tabular-nums">
              420 ₴
            </span>
            <span className="font-mono text-[0.75rem] text-ink-dim line-through">
              990 ₴
            </span>
          </div>
          <div className="relative h-2 rounded-full bg-ink/10">
            <div className="h-2 w-1/2 rounded-full bg-primary" />
            <div className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow-card" />
          </div>
          <div className="mt-1.5 flex justify-between font-mono text-[0.625rem] text-ink-faint">
            <span>7 днів</span>
            <span>30 днів</span>
            <span>60 днів</span>
          </div>
          <Cta label="Оформити 30 днів" />
        </Sheet>
      </Frame>

      <div className="pb-6 text-center text-[0.6875rem] text-ink-faint">
        <Icon name="flag" size={12} color="#A8A29C" /> макети, не робочі екрани
      </div>
    </div>
  );
}
